import jobsRegistry from "../generated/jobs.json";
import type { AdminUser, AuditEvent, FeatureFlag, JobRecord } from "../types/models";
import { createIdempotencyKey } from "../lib/idempotency";
import { apiData, apiPath, type Schema } from "./api";

export async function listAdminUsers(): Promise<AdminUser[]> {
  const data = await apiData<Schema<"AdminUserResponseListEnvelope">>("/admin/users", { query: { limit: 100, offset: 0 } });
  return data.filter(hasId);
}

export async function updateAdminUser(id: string, body: Schema<"AdminUpdateUserRequest">): Promise<AdminUser | null> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"AdminUpdateUserRequest">>(apiPath(`/admin/users/${id}`), { method: "PATCH", body });
  return (await listAdminUsers()).find((user) => user.id === id) ?? null;
}

export async function deleteAdminUser(id: string): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">>(apiPath(`/admin/users/${id}`), { method: "DELETE" });
}

export async function inviteAdminUser(body: Schema<"AdminInviteUserRequest">): Promise<Schema<"InvitationResponse">> {
  return apiData<Schema<"InvitationResponseEnvelope">, Schema<"AdminInviteUserRequest">>("/admin/users/invitations", {
    method: "POST",
    body,
    idempotencyKey: createIdempotencyKey()
  });
}

export async function listAuditEvents(): Promise<AuditEvent[]> {
  const data = await apiData<Schema<"AuditLogResponseListEnvelope">>("/admin/audit-events", { query: { limit: 100, offset: 0 } });
  return data.filter(hasId);
}

export async function listJobs(): Promise<JobRecord[]> {
  return apiData<Schema<"AdminJobResponseListEnvelope">>("/admin/jobs", { query: { limit: 50, offset: 0 } });
}

export async function enqueueJob(type: string, payload: Record<string, unknown> = {}): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"AdminEnqueueJobRequest">>(apiPath(`/admin/jobs/${type}`), {
    method: "POST",
    body: { payload },
    idempotencyKey: createIdempotencyKey()
  });
}

export async function listFeatureFlags(): Promise<FeatureFlag[]> {
  const data = await apiData<Schema<"FeatureFlagResponseListEnvelope">>("/feature-flags");
  return data.filter(hasCode);
}

export async function upsertFeatureFlag(code: string, enabled: boolean, config: Record<string, unknown> = {}): Promise<FeatureFlag | null> {
  const data = await apiData<Schema<"FeatureFlagResponseEnvelope">, Schema<"UpsertFeatureFlagRequest">>(apiPath(`/feature-flags/${code}`), {
    method: "PUT",
    body: { enabled, config }
  });
  return data.code ? (data as FeatureFlag) : null;
}

export function availableJobTypes(): string[] {
  return jobsRegistry;
}

function hasId<T extends { id?: string }>(item: T): item is T & { id: string } {
  return Boolean(item.id);
}

function hasCode<T extends { code?: string }>(item: T): item is T & { code: string } {
  return Boolean(item.code);
}
